/**
 * Customer activity tracking.
 *
 * Events are POSTed to /api/activity/track, which records them against the identity the
 * server resolves from the request — nothing this module sends about who the user is, or
 * when something happened, is trusted by the backend.
 *
 * Every function here is fire-and-forget and swallows its own errors. Tracking must never
 * break, block or slow down what the customer was actually doing.
 */

const TRACK_ENDPOINT = '/api/activity/track';

/**
 * Sends one event. Never throws and never returns a rejected promise.
 *
 * @param {object} api      axios instance from AuthContext
 * @param {object} event    { eventType, symbol?, market?, durationMs?, details? }
 */
export const trackEvent = async (api, event) => {
  if (!api || !event || !event.eventType) {
    return;
  }

  try {
    await api.post(TRACK_ENDPOINT, {
      eventType: event.eventType,
      symbol: event.symbol ?? null,
      market: event.market ?? null,
      durationMs: event.durationMs ?? null,
      details: event.details ?? {},
    });
  } catch (error) {
    // Deliberately console.debug, not console.error: a tracking failure is not something
    // the customer or a support engineer needs surfaced.
    console.debug('Activity tracking failed:', error?.message || error);
  }
};

/** A symbol was searched for or selected. */
export const trackTickerSearch = (api, symbol, market, resultCount) =>
  trackEvent(api, {
    eventType: 'ticker_search',
    symbol,
    market,
    details: resultCount != null ? { resultCount } : {},
  });

/** A chart was opened for a symbol. */
export const trackChartView = (api, symbol, market, details = {}) =>
  trackEvent(api, { eventType: 'chart_view', symbol, market, details });

/** The planetary overlay canvas was rendered over a chart. */
export const trackPlanetaryOverlayView = (api, symbol, market, details = {}) =>
  trackEvent(api, { eventType: 'planetary_overlay_view', symbol, market, details });

/** A report, chart image or data extract was downloaded. */
export const trackReportDownload = (api, symbol, format, details = {}) =>
  trackEvent(api, {
    eventType: 'report_download',
    symbol,
    details: { format, ...details },
  });

/** Two or more symbols were compared. */
export const trackChartComparison = (api, symbols, details = {}) =>
  trackEvent(api, {
    eventType: 'chart_comparison',
    symbol: Array.isArray(symbols) ? symbols.join(',') : symbols,
    details,
  });

/**
 * Backwards-compatible wrapper for the original tracker.
 *
 * Existing call sites pass (api, user, eventType, eventDetails). The `user` argument is now
 * ignored for attribution — the server derives identity from the request — but the signature
 * is preserved so the ~8 existing study dialogs keep working unchanged.
 *
 * It no longer bails out when `user` is missing. The old version returned early for anonymous
 * visitors, which meant trial and logged-out usage produced no record at all.
 */
export const logActivity = async (api, user, eventType, eventDetails = {}) => {
  // Tolerate the (user, eventType, details) call shape that crept into PlanetGroupDialog:
  // there, `api` is really the user and `user` is really the event type.
  if (typeof user === 'string' && typeof eventType !== 'string') {
    console.debug('logActivity called without an api client — event dropped:', user);
    return;
  }

  await trackEvent(api, {
    eventType: eventType || 'unknown',
    symbol: eventDetails?.symbol ?? eventDetails?.ticker ?? null,
    market: eventDetails?.market ?? null,
    details: eventDetails || {},
  });
};

export default trackEvent;
